import { Alert, Button, Progress } from '@heroui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import useSegment from '@/hooks/useSegment';
import useSurveyDrafts from '@/hooks/useSurveyDrafts';
import { usePostTrackerMutation } from '@/patient-app-common/api/trackerApi';
import { reviewHelperContext } from '@/patient-app-common/helpers/survey_helper';

import { SurveyLoader } from '../layout/SurveyLoader';
import { OpioidReview } from '../opioid/OpioidReview';

import { QuestionAnswer } from './QuestionAnswer';

const SUBMIT_SUCCESS_EVENT = 'Survey Completed';
const SUBMIT_FAIL_EVENT = 'Survey failed to submit';

export function Review({
  answers,
  eventId,
  handleEdit,
  idempotencyKey,
  medicineHistory,
  profile,
  questions,
  surveyData,
}) {
  const { t } = useTranslation('survey');
  const { sanitizeProfile, trackSurvey } = useSegment();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { saveDraft } = useSurveyDrafts();

  const [submitTracker] = usePostTrackerMutation();
  const submittingRef = useRef(false);

  const [loader, setLoader] = useState(null);
  const [reviewHelper, setReviewHelper] = useState(null);

  const opioidSurvey = surveyData.postprocessors.includes(
    'AtriumOpioidRiskModelPostprocessor'
  );

  useEffect(() => {
    const helper = reviewHelperContext({
      surveyData,
      answers,
      profile,
      medicineHistory,
    });
    setReviewHelper(helper);
  }, [answers, profile, surveyData, medicineHistory]);

  const eventType = useMemo(() => {
    if (eventId) {
      return 'app_event';
    }
    if (surveyData.mainTracker) {
      return 'main_tracker';
    }
    if (surveyData.setupSurvey) {
      return 'setup_survey';
    }
    if (surveyData.mainSurvey) {
      return 'main_survey';
    }
    return '';
  }, [surveyData, eventId]);

  const handleDraft = async () => {
    if (surveyData.editable) {
      saveDraft({
        answers,
        eventId,
        surveyData,
        index: 0,
      });
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;

    submittingRef.current = true;
    setLoader({ text: t('review.loading_text') });

    let params = reviewHelper.createSurveyParams(eventType, eventId);
    const photoParams = reviewHelper.createPhotoParams();
    if (photoParams && Object.keys(photoParams).length > 0) {
      params = {
        ...params,
        ...photoParams,
      };
    }
    params.idempotencyKey = idempotencyKey.current;

    try {
      const result = await submitTracker(params).unwrap();

      if (result.submitted) {
        handleDraft();

        trackSurvey(
          SUBMIT_SUCCESS_EVENT,
          {
            id: eventId,
            profile: sanitizeProfile(profile),
          },
          surveyData
        );

        setLoader({ text: t('review.loading_text') });

        navigate(`/recommendation/${surveyData.id}`, {
          state: {
            ...state,
            surveyData,
          },
        });
      } else {
        submittingRef.current = false;
        trackSurvey(
          SUBMIT_FAIL_EVENT,
          {
            id: eventId,
            profile: sanitizeProfile(profile),
          },
          surveyData
        );
        setLoader({
          text: `${t('common.error.server')} \nError code: 404`,
          type: 'error',
        });
      }
    } catch (e) {
      submittingRef.current = false;
      console.error(e);
      trackSurvey(
        SUBMIT_FAIL_EVENT,
        {
          id: eventId,
          profile: sanitizeProfile(profile),
        },
        surveyData
      );
      setLoader({
        text: `${t('common.error.server')} \nError code: ${e.status} ${e.data?.error ? `(${e.data?.error})` : ''}`,
        type: 'error',
      });
    }
  };

  if (loader) {
    return (
      <SurveyLoader
        text={loader.text}
        type={loader.type}
        handleBack={() => setLoader(null)}
      />
    );
  }

  if (opioidSurvey) {
    return (
      <OpioidReview
        answers={answers}
        eventId={eventId}
        handleEdit={handleEdit}
        idempotencyKey={idempotencyKey}
        profile={profile}
        questions={questions}
        surveyData={surveyData}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <header className="bg-background">
        <div className="flex justify-between p-4 items-center">
          <h1 className="font-light text-xl">{t('review.title')}</h1>
        </div>
        <Progress
          aria-label="100%"
          className=""
          value={100}
          color="success"
          radius="none"
        />
      </header>
      <main className="flex justify-center">
        <div className="flex flex-col w-full max-w-[800px] text-left p-12 space-y-4">
          <h2 className="font-bold">{t('review.title')}</h2>
          <Alert
            className="flex items-center justify-center w-full gap-4"
            color="warning"
            description={t('review.description')}
            endContent={
              <Button color="primary" onPress={handleSubmit} size="md">
                {t('common.action.submit')}
              </Button>
            }
            variant="faded"
          />
          <QuestionAnswer
            answers={answers}
            handleEdit={handleEdit}
            questions={questions}
          />
        </div>
      </main>
      <footer className="flex justify-center p-4 space-x-4">
        <Button
          className="w-1/2 md:w-1/4"
          color="primary"
          onPress={handleSubmit}
          size="lg"
        >
          {t('common.action.submit')}
        </Button>
      </footer>
    </div>
  );
}
